import { Box, Button, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LogDropzone from "./components/logAnalyzer/LogDropzone";
import { uploadPublicLogOrDemo } from "./components/logAnalyzer/api/publicTelemetry";
import { useAuthGate } from "../hooks/useAuthGate";
import PublicHeader, { joinAsDeveloper } from "./PublicHeader";
import { DOWNLOAD_CATALOG, DOWNLOADS_PUBLIC_PATH, downloadPath } from "./components/downloads/catalog";
import { ROUTES } from "../routes";
import { PUBLIC_BASE as LOG_PUBLIC_BASE } from "./components/logAnalyzer/paths";
import { PUBLIC_BASE as PROPULSION_PUBLIC_BASE } from "./components/propulsionCalc/paths";
import { PUBLIC_BASE as MOTOR_PUBLIC_BASE } from "./components/motorData/catalog";

const ACCENT = "#3a8eff";
const PUBLIC_ANALYZER = LOG_PUBLIC_BASE;

// Publicly explorable entry points — no sign-in needed to open these.
// `eyebrow` is the NVIDIA-style category label above the card title.
const TOOLS = [
  {
    eyebrow: "Analyze",
    title: "Log Analyzer",
    desc: "Upload ArduPilot flight logs for instant summaries, charts and AI insight — or browse logs the community has shared.",
    href: LOG_PUBLIC_BASE,
    cta: "Explore logs",
  },
  {
    eyebrow: "Simulate",
    title: "Propulsion Calculator",
    desc: "Size a motor + propeller + ESC + battery live in your browser — thrust, flight time, TWR, gauges and charts. Pin setups and compare.",
    href: PROPULSION_PUBLIC_BASE,
    cta: "Open calculator",
  },
  {
    eyebrow: "Compare",
    title: "Motor Test Data",
    desc: "What the manufacturer's datasheet claims vs what the motor actually did on our thrust stand — thrust, current, efficiency and torque at every throttle point.",
    href: MOTOR_PUBLIC_BASE,
    cta: "Compare claims",
  },
  {
    eyebrow: "Learn",
    title: "Documentation",
    desc: "Guides, API references and tutorials for building on the Vecros developer platform.",
    href: "https://docs.vecros.com/",
    external: true,
    cta: "Read the docs",
  },
];

// Browse-by-topic tiles — each routes to the surface that covers it today.
const TOPICS = [
  { title: "Flight Log Analysis", desc: "Summaries, charts, timelines, AI insight", href: LOG_PUBLIC_BASE },
  { title: "Propulsion & Powertrain", desc: "Motors, props, ESCs — thrust & efficiency", href: PROPULSION_PUBLIC_BASE },
  { title: "Batteries & Endurance", desc: "Packs, C-rates, flight-time budgets", href: PROPULSION_PUBLIC_BASE },
  { title: "Airframes & Profiles", desc: "450s to X8s, DJI presets, frame fit", href: PROPULSION_PUBLIC_BASE },
  { title: "Thrust-Stand Testing", desc: "Bench measurements & verified curves", href: MOTOR_PUBLIC_BASE },
  { title: "Fleet & Devices", desc: "Register, monitor and reach your drones", href: ROUTES.devices },
];

// Real, recently shipped platform updates (NVIDIA-style dated news strip).
const UPDATES = [
  {
    date: "Aug 2026",
    title: "Claimed vs measured: motor test data",
    desc: "Manufacturer thrust tables published side by side with our own thrust-stand runs, with the delta on every throttle point.",
    href: MOTOR_PUBLIC_BASE,
  },
  {
    date: "Aug 2026",
    title: "Setup comparisons: pin, save, share",
    desc: "Pin propulsion setups, compare them side-by-side with best-value highlighting, save named comparisons and share exact configurations by link.",
    href: PROPULSION_PUBLIC_BASE,
  },
  {
    date: "Aug 2026",
    title: "Community parts database",
    desc: "Submit motors, propellers, ESCs, batteries and drone frames for the shared catalog — reviewed, then available to every builder.",
    href: PROPULSION_PUBLIC_BASE,
  },
  {
    date: "Jul 2026",
    title: "Public flight-log gallery",
    desc: "Browse community-shared ArduPilot logs with summaries, charts and timelines — no account needed.",
    href: LOG_PUBLIC_BASE,
  },
];

/**
 * Public landing shown at the app root for logged-out visitors (see
 * ProtectedRoute) instead of bouncing straight to the accounts login. Lets
 * developers explore the public tools and hooks them into signing in / joining.
 */
const DeveloperLanding = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isAuthenticated } = useAuthGate();

  const colors = {
    page: isDark ? "#000000" : "#ffffff",
    surface: isDark ? "#05070b" : "#ffffff",
    border: isDark ? "#1c1c1c" : "#e2e8f0",
    heading: isDark ? "#ffffff" : "#0f172a",
    text: isDark ? "#c8d8e8" : "#334155",
    muted: isDark ? "#5b6b82" : "#64748b",
  };

  const hero = isDark
    ? "radial-gradient(circle at 50% 20%, rgba(16,55,129,.30) 0%, rgba(4,9,26,.95) 60%, #000 100%)"
    : "radial-gradient(circle at 50% 20%, rgba(58,142,255,.14) 0%, rgba(248,250,252,.85) 55%, #fff 85%)";

  // A card that navigates IS a link: it needs a real href so cmd-click,
  // middle-click and crawlers work. External targets stay plain anchors.
  const linkProps = (href, external) =>
    external
      ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" }
      : { component: RouterLink, to: href };
  const cardLink = { textDecoration: "none", color: "inherit", display: "block" };

  return (
    <Box sx={{ minHeight: "100vh", background: colors.page, color: colors.text, overflow: "auto" }}>
      <PublicHeader sticky accent={ACCENT}>
        <Box
          sx={{
            px: { xs: 2.5, md: 4 },
            height: 38,
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            fontSize: 12.5,
            fontWeight: 600,
            borderTop: `1px solid ${colors.border}`,
            color: colors.muted,
          }}
        >
          <Box component="a" href="#topics" sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: ACCENT } }}>
            Topics
          </Box>
          <Box component="a" href="#tools" sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: ACCENT } }}>
            Platforms &amp; Tools
          </Box>
          <Box component="a" href="#downloads" sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: ACCENT } }}>
            Downloads
          </Box>
          <Box component="a" href="#updates" sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: ACCENT } }}>
            What's New
          </Box>
        </Box>
      </PublicHeader>

      {/* Hero — centered, two-tone headline, product artifact + trust bullets */}
      <Box sx={{ background: hero, px: { xs: 3, md: 7 }, pt: { xs: 7, md: 10 }, pb: { xs: 6, md: 8 }, borderBottom: `1px solid ${colors.border}`, textAlign: "center" }}>
        <Box
          component="h1"
          sx={{ m: 0, fontSize: { xs: 36, md: 54 }, fontWeight: 700, lineHeight: 1.12, letterSpacing: "-0.01em", color: colors.heading }}
        >
          Skip the guesswork.{" "}
          <Box component="span" sx={{ color: ACCENT }}>
            Start flying.
          </Box>
        </Box>
        <Box component="p" sx={{ mt: 2.5, mb: 0, mx: "auto", fontSize: { xs: 15, md: 18 }, color: colors.muted, lineHeight: 1.6, maxWidth: 640 }}>
          Analyze flight logs, size your propulsion and validate a build before it leaves the bench —
          free, in your browser, no account needed to explore.
        </Box>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1.5 }}>
          <Button
            component={RouterLink}
            to={PROPULSION_PUBLIC_BASE}
            variant="contained"
            sx={{ backgroundColor: ACCENT, textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3.5, py: 1.2, "&:hover": { backgroundColor: ACCENT, filter: "brightness(0.95)" } }}
          >
            Try the tools — no sign-up
          </Button>
          <Button
            onClick={() => (isAuthenticated ? window.location.assign(ROUTES.developer) : joinAsDeveloper())}
            variant="outlined"
            sx={{ color: ACCENT, borderColor: ACCENT, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, py: 1.2, "&:hover": { borderColor: ACCENT, background: `${ACCENT}14` } }}
          >
            {isAuthenticated ? "Access the app" : "Join as developer"}
          </Button>
        </Box>

        {/* Product artifact: the real log dropzone — this is the front door.
            Signed in it uploads to the account; logged out it uploads
            anonymously (public log, one free analysis) and lands on the public
            detail page. */}
        <Box sx={{ mx: "auto", mt: 5, maxWidth: 640, textAlign: "left" }}>
          <LogDropzone
            upload={isAuthenticated ? undefined : uploadPublicLogOrDemo}
            onUploaded={(id) =>
              window.location.assign(
                isAuthenticated ? `${ROUTES.logAnalyzer}/${id}` : `${PUBLIC_ANALYZER}/${id}`,
              )
            }
          />
          <Box sx={{ mt: 1.5, textAlign: "center", fontSize: 12.5, color: colors.muted }}>
            {isAuthenticated ? (
              "Summaries, charts, timeline and AI insight — free."
            ) : (
              <>
                Drop your log and we'll analyze it — no account. Uploads without one are public; sign in to keep yours
                private. Or open a{" "}
                <Box
                  component="a"
                  href={PUBLIC_ANALYZER}
                  sx={{ color: ACCENT, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                >
                  community flight log
                </Box>{" "}
                to see the output first.
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 3, fontSize: 13, color: colors.muted }}>
          <Box>✓ Free, in-browser tools</Box>
          <Box>✓ Community parts database</Box>
          <Box>✓ Share &amp; compare setups</Box>
        </Box>
      </Box>

      {/* Browse by topic */}
      <Box id="topics" sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto" }}>
        <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: colors.muted, mb: 2.5 }}>BROWSE BY TOPIC</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 1.5 }}>
          {TOPICS.map((t) => (
            <Box
              key={t.title}
              {...linkProps(t.href)}
              sx={{
                p: 2,
                borderRadius: 2,
                ...cardLink,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                transition: "border-color .15s, transform .15s",
                "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
              }}
            >
              <Box sx={{ fontSize: 14.5, fontWeight: 700, color: colors.heading, mb: 0.5 }}>{t.title}</Box>
              <Box sx={{ fontSize: 12.5, color: colors.muted, lineHeight: 1.5 }}>{t.desc}</Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Tools */}
      <Box id="tools" sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto", borderTop: `1px solid ${colors.border}` }}>
        <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: colors.muted, mb: 2.5 }}>PLATFORMS &amp; TOOLS</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2.5 }}>
          {TOOLS.map((t) => (
            <Box
              key={t.title}
              {...linkProps(t.href, t.external)}
              sx={{
                p: 3,
                borderRadius: 2,
                ...cardLink,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                transition: "border-color .15s, transform .15s",
                "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
              }}
            >
              <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: ACCENT, fontWeight: 700, mb: 1 }}>
                {t.eyebrow.toUpperCase()}
              </Box>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: colors.heading, mb: 1 }}>{t.title}</Box>
              <Box sx={{ fontSize: 13.5, color: colors.muted, lineHeight: 1.6, mb: 2, minHeight: 66 }}>{t.desc}</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: ACCENT, fontSize: 13.5, fontWeight: 600 }}>
                {t.cta}
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Downloads */}
      <Box id="downloads" sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto", borderTop: `1px solid ${colors.border}` }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: colors.muted }}>DOWNLOADS</Box>
          <Box
            component="a"
            href={DOWNLOADS_PUBLIC_PATH}
            sx={{ fontSize: 13, fontWeight: 600, color: ACCENT, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            All downloads
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 2.5 }}>
          {DOWNLOAD_CATALOG.map((d) => (
            <Box
              key={d.slug}
              {...linkProps(downloadPath(d.slug))}
              sx={{
                p: 3,
                borderRadius: 2,
                ...cardLink,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                transition: "border-color .15s, transform .15s",
                "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
              }}
            >
              <Box sx={{ fontSize: 11, letterSpacing: ".12em", color: ACCENT, fontWeight: 700, mb: 1 }}>
                {d.category.toUpperCase()}
              </Box>
              <Box sx={{ fontSize: 17, fontWeight: 700, color: colors.heading, mb: 1 }}>{d.title}</Box>
              <Box sx={{ fontSize: 13.5, color: colors.muted, lineHeight: 1.6, mb: 2 }}>{d.tagline}</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: ACCENT, fontSize: 13.5, fontWeight: 600 }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
                {d.action.label}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* What's new */}
      <Box id="updates" sx={{ borderTop: `1px solid ${colors.border}` }}>
        <Box sx={{ px: { xs: 3, md: 7 }, py: { xs: 5, md: 6 }, maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ fontSize: 11, letterSpacing: ".14em", color: colors.muted, mb: 2.5 }}>WHAT'S NEW</Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2.5 }}>
            {UPDATES.map((u) => (
              <Box
                key={u.title}
                {...linkProps(u.href)}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  ...cardLink,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  transition: "border-color .15s, transform .15s",
                  "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
                }}
              >
                <Box sx={{ fontSize: 12, color: colors.muted, mb: 1 }}>{u.date}</Box>
                <Box sx={{ fontSize: 16, fontWeight: 700, color: colors.heading, mb: 1, lineHeight: 1.35 }}>{u.title}</Box>
                <Box sx={{ fontSize: 13.5, color: colors.muted, lineHeight: 1.6, mb: 2 }}>{u.desc}</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: ACCENT, fontSize: 13.5, fontWeight: 600 }}>
                  Try it
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 5, fontSize: 13, color: colors.muted }}>
            No account needed to explore. <Box component="span" sx={{ color: colors.text }}>Join as a developer</Box> to
            upload your own logs, save setups &amp; comparisons across devices, register devices and get developer updates.
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DeveloperLanding;
